import { useState, useMemo } from "react";
import {
    Box,
    Typography,
    Card,
    CardContent,
    Chip,
    Button,
    IconButton,
    TextField,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Paper,
    Stack,
    Divider,
    Tooltip,
    Avatar,
    Badge,
    InputAdornment,
    Alert,
    Snackbar,
    Grid,
} from "@mui/material";

import SearchIcon from "@mui/icons-material/Search";
import FilterListIcon from "@mui/icons-material/FilterList";
import DownloadIcon from "@mui/icons-material/Download";
import VisibilityIcon from "@mui/icons-material/Visibility";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import RefreshIcon from "@mui/icons-material/Refresh";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import PhoneAndroidIcon from "@mui/icons-material/PhoneAndroid";
import CloseIcon from "@mui/icons-material/Close";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { useCallback } from "react";
import { useEffect } from "react";
import apiClient from "../utils/apiClient";
import { toast } from "react-toastify";

// ─── Mock Data ────────────────────────────────────────────────────────────────

const getAllData = async (params = {}) => {
    const res = await apiClient.get("/api/admin/transactions", { params });
    const data = res?.data
    return data;
};

const updateTransactionStatus = async (
    id,
    payload
) => {
    return await apiClient.put(
        `/api/admin/transactions/${id}`,
        payload
    );
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

const fmt = (n) =>
    "₹" + Number(n).toLocaleString("en-IN", { minimumFractionDigits: 2 });

const fmtDate = (iso) =>
    new Date(iso).toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
    });

const initials = (name) =>
    name
        .split(" ")
        .slice(0, 2)
        .map((w) => w[0])
        .join("")
        .toUpperCase();

const avatarColor = (name) => {
    const colors = ["#1976d2", "#388e3c", "#f57c00", "#7b1fa2", "#c62828", "#00796b"];
    let hash = 0;
    for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
    return colors[Math.abs(hash) % colors.length];
};

// ─── Status Chip ──────────────────────────────────────────────────────────────

const STATUS_CONFIG = {
    pending: { color: "warning", label: "Pending" },
    approved: { color: "info", label: "Approved" },
    processing: { color: "secondary", label: "Processing" },
    completed: { color: "success", label: "Completed" },
    rejected: { color: "error", label: "Rejected" },
    cancelled: { color: "default", label: "Cancelled" },
    failed: { color: "error", label: "Failed" },
};

const StatusChip = ({ status }) => {
    const cfg = STATUS_CONFIG[status] || { color: "default", label: status };
    return (
        <Chip
            label={cfg.label}
            color={cfg.color}
            size="small"
            sx={{ fontWeight: 600, fontSize: "0.72rem", textTransform: "capitalize" }}
        />
    );
};

// ─── Method Badge ─────────────────────────────────────────────────────────────

const MethodBadge = ({ method }) => (
    <Stack direction="row" alignItems="center" spacing={0.5}>
        {method === "upi" ? (
            <PhoneAndroidIcon sx={{ fontSize: 15, color: "text.secondary" }} />
        ) : (
            <AccountBalanceIcon sx={{ fontSize: 15, color: "text.secondary" }} />
        )}
        <Typography variant="caption" color="text.secondary" sx={{ textTransform: "uppercase", fontWeight: 600, letterSpacing: 0.5 }}>
            {method === "upi" ? "UPI" : "Bank Transfer"}
        </Typography>
    </Stack>
);

// ─── Stat Card ────────────────────────────────────────────────────────────────

const StatCard = ({ label, value, color = "text.primary", icon, subtitle }) => (
    <Card elevation={0} sx={{ border: "1px solid", borderColor: "divider", borderRadius: 2 }}>
        <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
            <Stack direction="row" alignItems="flex-start" justifyContent="space-between">
                <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.8, fontSize: "0.7rem" }}>
                        {label}
                    </Typography>
                    <Typography variant="h5" sx={{ fontWeight: 700, color, mt: 0.5, lineHeight: 1.2 }}>
                        {value}
                    </Typography>
                    {subtitle && (
                        <Typography variant="caption" color="text.disabled" sx={{ mt: 0.5, display: "block" }}>
                            {subtitle}
                        </Typography>
                    )}
                </Box>
                <Box sx={{ p: 1, borderRadius: 2, bgcolor: "action.hover" }}>{icon}</Box>
            </Stack>
        </CardContent>
    </Card>
);

// ─── Detail Row ───────────────────────────────────────────────────────────────

const DetailRow = ({ label, value, valueColor }) => (
    <Box>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" py={1.2}>
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500, minWidth: 160 }}>
                {label}
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 600, color: valueColor || "text.primary", textAlign: "right", maxWidth: "55%", wordBreak: "break-all" }}>
                {value || "—"}
            </Typography>
        </Stack>
        <Divider />
    </Box>
);

// ─── View Detail Dialog ───────────────────────────────────────────────────────

const ViewDialog = ({ txn, open, onClose, fetchData }) => {
    const [status, setStatus] = useState("");
    const [referenceId, setReferenceId] = useState("");
    const [reason, setReason] = useState("");
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState({});

    useEffect(() => {
        if (txn) {
            setStatus("");
            setReferenceId(txn.transactionReference || "");
            setReason(txn.failedReason || txn.rejectionReason || "");
        }
    }, [txn]);

    if (!txn) return null;


    const validate = () => {
        const newErrors = {};

        if (!status) {
            newErrors.status = "Please select status";
        }

        if (
            ["completed"].includes(status) &&
            !referenceId
        ) {
            newErrors.referenceId = "Reference ID is required";
        }

        if (
            ["failed", "rejected"].includes(status) &&
            !reason
        ) {
            newErrors.reason = "Reason is required";
        }

        setErrors(newErrors);

        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async () => {
        if (!validate()) return;

        try {
            setLoading(true);

            const payload = {
                status,
                transactionReference: referenceId,
                failedReason: status === "failed" ? reason : null,
                rejectionReason: status === "rejected" ? reason : null,
            };
            await updateTransactionStatus(txn.transactionId, payload)

            toast.success("Transaction updated successfully");
            fetchData()
            onClose();
        } catch (error) {
            toast.error(
                error?.response?.data?.message ||
                "Failed to update transaction"
            );
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ elevation: 3, sx: { borderRadius: 3 } }}>
            <DialogTitle sx={{ pb: 1 }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <Stack direction="row" alignItems="center" spacing={1.5}>
                        <Avatar sx={{ bgcolor: avatarColor(txn.vendorName), width: 36, height: 36, fontSize: "0.85rem", fontWeight: 700 }}>
                            {initials(txn.vendorName)}
                        </Avatar>
                        <Box>
                            <Typography variant="subtitle1" fontWeight={700}>{txn.vendorName} <StatusChip status={txn.status} /></Typography>
                            <Typography variant="caption" color="text.secondary">{txn.vendorEmail}</Typography>
                        </Box>
                    </Stack>
                    <IconButton size="small" onClick={onClose}><CloseIcon fontSize="small" /></IconButton>
                </Stack>
            </DialogTitle>
            <Divider />
            <DialogContent sx={{ pt: 2, pb: 1 }}>
                {/* <Stack direction="row" spacing={1} mb={2} flexWrap="wrap" useFlexGap>
                    <StatusChip status={txn.status} />
                    <Chip label={txn.type} size="small" variant="outlined" sx={{ textTransform: "capitalize" }} />
                    <Chip label={txn.transactionFlow} size="small" variant="outlined" color={txn.transactionFlow === "credit" ? "success" : "error"} sx={{ textTransform: "capitalize" }} />
                </Stack> */}

                <Paper variant="outlined" sx={{ borderRadius: 2, px: 2, py: 0.5, mb: 2 }}>

                    {/* <DetailRow label="Transaction ID" value={txn.transactionId} />
                    <DetailRow label="Wallet ID" value={txn.walletId} />
                    <DetailRow label="Vendor ID" value={txn.vendorId} /> */}
                    <DetailRow
                        label="Account Holder"
                        value={txn?.payoutDetails?.accountHolderName}
                    />
                    {txn.paymentMethod === "upi" ? (
                        <>
                            <DetailRow
                                label="UPI ID"
                                value={txn?.payoutDetails?.upiId}
                            />
                        </>
                    ) : (
                        <>
                            <DetailRow
                                label="Bank Name"
                                value={txn?.payoutDetails?.bankName}
                            />
                            <DetailRow
                                label="Account Number"
                                value={txn?.payoutDetails?.accountNumber}
                            />
                            <DetailRow
                                label="IFSC Code"
                                value={txn?.payoutDetails?.ifscCode}
                            />
                            <DetailRow
                                label="Branch Name"
                                value={txn?.payoutDetails?.branchName}
                            />
                        </>
                    )}

                    <DetailRow
                        label="Payout Approval Status"
                        value={txn?.payoutDetails?.approval_status}
                    />
                    <DetailRow
                        label="Payout Verified"
                        value={txn?.payoutDetails?.isVerified ? 'Yes' : 'No'}
                    />

                    <DetailRow label="Amount" value={fmt(txn.amount)} valueColor="primary.main" />
                    {/* <DetailRow label="Opening balance" value={fmt(txn.openingBalance)} />
                    <DetailRow label="Closing balance" value={fmt(txn.closingBalance)} /> */}
                    <DetailRow label="Payment method" value={txn.paymentMethod === "upi" ? "UPI" : "Bank Transfer"} />
                    <DetailRow label="Transaction reference" value={txn.transactionReference || "—"} />
                    <DetailRow label="Description" value={txn.description} />
                    {txn.createdAt && <DetailRow label="Created At" value={fmtDate(txn.createdAt)} />}
                    {txn.approvedAt && <DetailRow label="Approved At" value={fmtDate(txn.approvedAt)} />}
                    {txn.processedAt && <DetailRow label="Processed At" value={fmtDate(txn.processedAt)} />}
                    {txn.completedAt && <DetailRow label="Completed At" value={fmtDate(txn.completedAt)} />}
                    {txn.rejectedAt && <DetailRow label="Rejected At" value={fmtDate(txn.rejectedAt)} />}
                    {txn.failedAt && <DetailRow label="Failed At" value={fmtDate(txn.failedAt)} />}
                    {txn.rejectionReason && (
                        <DetailRow label="Rejection reason" value={txn.rejectionReason} valueColor="error.main" />
                    )}
                </Paper>

                {txn.failedReason && (
                    <DetailRow
                        label="Failed reason"
                        value={txn.failedReason}
                        valueColor="error.main"
                    />
                )}
                {txn.rejectionReason && (
                    <Alert severity="error" variant="outlined" sx={{ borderRadius: 2, fontSize: "0.82rem" }}>
                        {txn.rejectionReason}
                    </Alert>
                )}

                {/* STATUS BUTTONS */}
                {!["completed", "failed", "rejected", "cancelled"].includes(
                    txn?.status
                ) && (
                        <Typography
                            variant="subtitle2"
                            fontWeight={700}
                            mb={1.2}
                        >
                            Update Status
                        </Typography>
                    )}
                <Stack
                    direction="row"
                    spacing={1}
                    flexWrap="wrap"
                    useFlexGap
                    mb={2.5}
                >

                    {/* =========================
       PENDING -> APPROVE / REJECT
    ========================== */}
                    {txn?.status === "pending" && (
                        <>
                            <Button
                                variant={
                                    status === "approved"
                                        ? "contained"
                                        : "outlined"
                                }
                                color="success"
                                onClick={() => setStatus("approved")}
                            >
                                Approve
                            </Button>

                            <Button
                                variant={
                                    status === "rejected"
                                        ? "contained"
                                        : "outlined"
                                }
                                color="error"
                                onClick={() => setStatus("rejected")}
                            >
                                Reject
                            </Button>
                        </>
                    )}

                    {/* =========================
       APPROVED -> PROCESSING
    ========================== */}
                    {txn?.status === "approved" && (
                        <Button
                            variant={
                                status === "processing"
                                    ? "contained"
                                    : "outlined"
                            }
                            color="warning"
                            onClick={() => setStatus("processing")}
                        >
                            Processing
                        </Button>
                    )}

                    {/* =========================
       PROCESSING -> COMPLETE / FAILED
    ========================== */}
                    {txn?.status === "processing" && (
                        <>
                            <Button
                                variant={
                                    status === "completed"
                                        ? "contained"
                                        : "outlined"
                                }
                                color="primary"
                                onClick={() => setStatus("completed")}
                            >
                                Complete
                            </Button>

                            <Button
                                variant={
                                    status === "failed"
                                        ? "contained"
                                        : "outlined"
                                }
                                color="error"
                                onClick={() => setStatus("failed")}
                            >
                                Failed
                            </Button>
                        </>
                    )}
                 
                    {/* =========================
       FINAL STATUS MESSAGE
    ========================== */}

                    {["completed", "failed", "rejected"].includes(
                        txn?.status
                    ) && (
                            <Alert
                                severity={
                                    txn?.status === "completed"
                                        ? "success"
                                        : "error"
                                }
                                sx={{
                                    width: "100%",
                                    borderRadius: 2,
                                }}
                            >
                                This transaction is already{" "}
                                <b>{txn?.status}</b>.
                                No further action allowed.
                            </Alert>
                        )}
                </Stack>
                    {errors.status && <Alert
                        severity={'error'}
                        sx={{
                            mt: 3,
                            borderRadius: 2,
                        }}
                    >
                        {errors.status}
                    </Alert>}
                {/* REFERENCE ID */}
                {["completed"].includes(status) && (
                    <TextField
                        fullWidth
                        label={
                            status === "completed"
                                ? "Completion Reference ID"
                                : status === "processing"
                                    ? "Processing Reference ID"
                                    : "Approval Reference ID"
                        }
                        value={referenceId}
                        onChange={(e) =>
                            setReferenceId(e.target.value)
                        }
                        error={!!errors.referenceId}
                        helperText={errors.referenceId}
                        sx={{ mb: 2 }}
                    />
                )}

                {/* FAILED / REJECT REASON */}
                {["failed", "rejected"].includes(status) && (
                    <TextField
                        fullWidth
                        multiline
                        rows={4}
                        label={
                            status === "failed"
                                ? "Failed Reason"
                                : "Rejection Reason"
                        }
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                        error={!!errors.reason}
                        helperText={errors.reason}
                    />
                )}

                {/* STATUS ALERT */}
                {status && (
                    <Alert
                        severity={
                            status === "completed"
                                ? "success"
                                : status === "failed"
                                    ? "error"
                                    : status === "rejected"
                                        ? "warning"
                                        : "info"
                        }
                        sx={{
                            mt: 3,
                            borderRadius: 2,
                        }}
                    >
                        Selected Status: <b>{status}</b>
                    </Alert>
                )}
            </DialogContent>
            <Divider />
            <DialogActions sx={{ p: 2 }}>
                <Button
                    onClick={onClose}
                    variant="outlined"
                    color="inherit"
                >
                    Cancel
                </Button>

                {/* SHOW SUBMIT BUTTON ONLY WHEN ACTION AVAILABLE */}

                {!["completed", "failed", "rejected", "cancelled"].includes(
                    txn?.status
                ) && (
                        <Button
                            variant="contained"
                            onClick={handleSubmit}
                            disabled={loading}
                            sx={{
                                minWidth: 140,
                                borderRadius: 2,
                                textTransform: "none",
                                fontWeight: 600,
                            }}
                        >
                            {loading ? "Updating..." : "Submit"}
                        </Button>
                    )}
            </DialogActions>
        </Dialog>
    );
};

// ─── Main Component ───────────────────────────────────────────────────────────

export default function PayoutManagement() {
    const [txns, setTxns] = useState([]);
    const [stats, setStats] = useState({
        total: 0,
        pending: 0,
        completed: 0,
        rejected: 0,
    });
    const [totalCount, setTotalCount] = useState(0);
    const [search, setSearch] = useState("");
    const [filterStatus, setFilterStatus] = useState("");
    const [filterMethod, setFilterMethod] = useState("");
    // const [sortBy, setSortBy] = useState("newest");
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(8);

    const [viewTxn, setViewTxn] = useState(null);
    const [viewOpen, setViewOpen] = useState(false);
    const [rejectOpen, setRejectOpen] = useState(false);
    const [loading, setLoading] = useState(true);

    const [snack, setSnack] = useState({ open: false, msg: "", severity: "success" });

    const notify = (msg, severity = "success") => setSnack({ open: true, msg, severity });


    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const res = await getAllData({
                search,
                page: page + 1,
                limit: rowsPerPage,
                status: filterStatus,
                method: filterMethod,
                // refundType: filterType,
            });

            console.log(res, 'ooo')
            const formatted = (res?.data || []).map((item) => ({
                ...item,

                vendorName: item?.vendor?.name || "N/A",
                vendorEmail: item?.vendor?.email || "N/A",

                rejectionReason:
                    item?.rejectionReason ||
                    item?.failedReason ||
                    "",

                payoutDetails: item?.vendor?.payoutDetails || {},
                wallet: item?.vendor?.wallet || {},
            }));

            setTxns(formatted);

            setStats({
                total: Number(res?.stats?.totalPayouts || 0),
                pending: Number(res?.stats?.pendingPayouts || 0),
                completed: Number(res?.stats?.completedPayouts || 0),
                rejected: Number(res?.stats?.rejectedPayouts || 0),
            });

            setTotalCount(res?.total || 0);
        } catch (err) {
            toast.error("Failed to load refunds");
        } finally {
            setLoading(false);
        }
    }, [search, page, filterStatus, rowsPerPage, filterMethod]);

    // userId,

    useEffect(() => { fetchData(); }, [fetchData]);
    // ── Filter + Sort ──

    const handleReset = () => {
        setSearch("");
        setFilterStatus("");
        setFilterMethod("");
        // setSortBy("newest");
        setPage(0);
    };

    // const handleExport = () => {
    //     const headers = ["Transaction ID", "Vendor", "Email", "Amount", "Method", "Status", "Reference", "Date", "Description", "Rejection Reason"];
    //     const rows = filtered.map((t) => [
    //         t.transactionId, t.vendorName, t.vendorEmail, t.amount,
    //         t.paymentMethod, t.status, t.transactionReference,
    //         fmtDate(t.createdAt), t.description, t.rejectionReason,
    //     ]);
    //     const csv = [headers, ...rows].map((r) => r.map((c) => `"${c}"`).join(",")).join("\n");
    //     const a = document.createElement("a");
    //     a.href = "data:text/csv;charset=utf-8," + encodeURIComponent(csv);
    //     a.download = "payouts_export.csv";
    //     a.click();
    //     notify("CSV exported successfully.");
    // };

    // ── Render ──

    return (
        <Box>

            {/* Header */}
            <Stack direction="row" alignItems="center" justifyContent="space-between" mb={3} flexWrap="wrap" gap={1}>
                <Stack direction="row" alignItems="center" spacing={1.5}>
                    <AccountBalanceWalletIcon sx={{ fontSize: 28, color: "primary.main" }} />
                    <Typography variant="h5" fontWeight={700}>Payout management</Typography>
                    {/* {stats.pendingCount > 0 && (
                        <Chip label={`${stats.pendingCount} pending`} color="warning" size="small" sx={{ fontWeight: 700 }} />
                    )} */}
                </Stack>
                {/* <Stack direction="row" spacing={1}>
                    <Tooltip title="Refresh">
                        <IconButton size="small" sx={{ border: "1px solid", borderColor: "divider", borderRadius: 2 }}>
                            <RefreshIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                    <Button variant="outlined" size="small" startIcon={<DownloadIcon />} onClick={handleExport} sx={{ borderRadius: 2, textTransform: "none", fontWeight: 600 }}>
                        Export CSV
                    </Button>
                </Stack> */}
            </Stack>

            {/* Stat Cards */}
            <Grid container spacing={2} mb={3}>
                <Grid item size={{ xs: 12, sm: 6, md: 3 }} >
                    <StatCard label="Total payouts" value={fmt(stats.total)} subtitle="All time" icon={<AccountBalanceWalletIcon color="primary" sx={{ fontSize: 22 }} />} />
                </Grid>
                <Grid item size={{ xs: 12, sm: 6, md: 3 }}>
                    <StatCard label="Pending" value={fmt(stats.pending)} color="warning.main" subtitle="Awaiting approval" icon={<InfoOutlinedIcon color="warning" sx={{ fontSize: 22 }} />} />
                </Grid>
                <Grid item size={{ xs: 12, sm: 6, md: 3 }}>
                    <StatCard label="Completed" value={fmt(stats.completed)} color="success.main" subtitle="Paid out" icon={<CheckCircleOutlineIcon color="success" sx={{ fontSize: 22 }} />} />
                </Grid>
                <Grid item size={{ xs: 12, sm: 6, md: 3 }}>
                    <StatCard label="Rejected" value={fmt(stats.rejected)} color="error.main" subtitle="Declined" icon={<CancelOutlinedIcon color="error" sx={{ fontSize: 22 }} />} />
                </Grid>
            </Grid>

            {/* Filters */}
            <Paper elevation={0} sx={{ border: "1px solid", borderColor: "divider", borderRadius: 2, p: 2, mb: 2 }}>
                <Stack direction="row" alignItems="center" spacing={1} mb={1.5}>
                    <FilterListIcon fontSize="small" color="action" />
                    <Typography variant="body2" fontWeight={600} color="text.secondary">Filters</Typography>
                </Stack>
                <Stack direction="row" flexWrap="wrap" gap={1.5} alignItems="center">
                    <TextField
                        size="small"
                        placeholder="Search vendor, txn ID, reference..."
                        value={search}
                        onChange={(e) => { setSearch(e.target.value); setPage(0); }}
                        InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
                        sx={{ minWidth: 260, flex: 1, "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                    />
                    <FormControl size="small" sx={{ minWidth: 150 }}>
                        <InputLabel>Status</InputLabel>
                        <Select value={filterStatus} label="Status" onChange={(e) => { setFilterStatus(e.target.value); setPage(0); }} sx={{ borderRadius: 2 }}>
                            <MenuItem value="">All status</MenuItem>
                            {Object.entries(STATUS_CONFIG).map(([k, v]) => (
                                <MenuItem key={k} value={k}>{v.label}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <FormControl size="small" sx={{ minWidth: 150 }}>
                        <InputLabel>Method</InputLabel>
                        <Select value={filterMethod} label="Method" onChange={(e) => { setFilterMethod(e.target.value); setPage(0); }} sx={{ borderRadius: 2 }}>
                            <MenuItem value="">All methods</MenuItem>
                            <MenuItem value="upi">UPI</MenuItem>
                            <MenuItem value="bank_transfer">Bank Transfer</MenuItem>
                        </Select>
                    </FormControl>
                    <Button variant="outlined" size="small" onClick={handleReset} sx={{ borderRadius: 2, textTransform: "none", height: 40, whiteSpace: "nowrap" }}>
                        Reset filters
                    </Button>
                </Stack>
            </Paper>

            {/* Table */}
            <Grid container spacing={2} mb={3}>
                <Grid item size={{ xs: 12 }}>
                    <Paper elevation={0} sx={{ border: "1px solid", borderColor: "divider", borderRadius: 2, overflow: "hidden" }}>
                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow sx={{ bgcolor: "action.hover" }}>
                                        {["Vendor", "Amount", "Method", "Status", "Reference", "Date", "Actions"].map((h) => (
                                            <TableCell key={h} sx={{ fontWeight: 700, fontSize: "0.72rem", textTransform: "uppercase", letterSpacing: 0.8, color: "text.secondary", py: 1.5, whiteSpace: "nowrap" }}>
                                                {h}
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {txns.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={8} align="center" sx={{ py: 6 }}>
                                                <Typography variant="body2" color="text.disabled">No transactions found</Typography>
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        txns.map((t) => (
                                            <TableRow key={t.transactionId} hover sx={{ "&:last-child td": { border: 0 } }}>
                                                {/* <TableCell sx={{ py: 1.5 }}>
                                                    <Typography variant="body2" fontWeight={600} sx={{ fontFamily: "monospace", fontSize: "0.78rem" }}>
                                                        {t.transactionId}
                                                    </Typography>
                                                    <Chip label={t.type} size="small" variant="outlined" sx={{ fontSize: "0.65rem", height: 18, mt: 0.5, textTransform: "capitalize" }} />
                                                </TableCell> */}

                                                <TableCell sx={{ py: 1.5 }}>
                                                    <Stack direction="row" alignItems="center" spacing={1}>
                                                        <Avatar sx={{ bgcolor: avatarColor(t.vendorName), width: 30, height: 30, fontSize: "0.7rem", fontWeight: 700 }}>
                                                            {initials(t.vendorName)}
                                                        </Avatar>
                                                        <Box>
                                                            <Typography variant="body2" fontWeight={600} noWrap sx={{ maxWidth: 130 }}>{t.vendorName}</Typography>
                                                            {/* <Typography variant="caption" color="text.secondary" noWrap sx={{ maxWidth: 130, display: "block" }}>{t.vendorId}</Typography> */}
                                                        </Box>
                                                    </Stack>
                                                </TableCell>

                                                <TableCell sx={{ py: 1.5 }}>
                                                    <Typography variant="body2" fontWeight={700} color="primary.main">
                                                        {fmt(t.amount)}
                                                    </Typography>
                                                    {/* <Typography variant="caption" color="text.disabled">
                                                        {t.transactionFlow === "debit" ? "↓ Debit" : "↑ Credit"}
                                                    </Typography> */}
                                                </TableCell>

                                                <TableCell sx={{ py: 1.5 }}>
                                                    <MethodBadge method={t.paymentMethod} />
                                                </TableCell>

                                                <TableCell sx={{ py: 1.5 }}>
                                                    <StatusChip status={t.status} />
                                                </TableCell>

                                                <TableCell sx={{ py: 1.5 }}>
                                                    {t.transactionReference ? (
                                                        <Tooltip title={t.transactionReference}>
                                                            <Typography variant="caption" sx={{ fontFamily: "monospace", color: "text.secondary", maxWidth: 120, display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                                                {t.transactionReference}
                                                            </Typography>
                                                        </Tooltip>
                                                    ) : (
                                                        <Typography variant="caption" color="text.disabled">—</Typography>
                                                    )}
                                                </TableCell>

                                                <TableCell sx={{ py: 1.5 }}>
                                                    <Typography variant="caption" color="text.secondary" noWrap>
                                                        {fmtDate(t.createdAt)}
                                                    </Typography>
                                                </TableCell>

                                                <TableCell sx={{ py: 1.5 }}>
                                                    <Stack direction="row" spacing={0.5}>
                                                        <Tooltip title="View details">
                                                            <IconButton size="small" color="info" onClick={() => { setViewTxn(t); setViewOpen(true); }}>
                                                                <VisibilityIcon sx={{ fontSize: 17 }} />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </Stack>
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>

                        <Divider />
                        <TablePagination
                            rowsPerPageOptions={[5, 8, 10, 25]}
                            component="div"
                            count={totalCount}
                            rowsPerPage={rowsPerPage}
                            page={page}
                            onPageChange={(_, p) => setPage(p)}
                            onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                            sx={{ "& .MuiTablePagination-toolbar": { fontSize: "0.82rem" } }}
                        />
                    </Paper>
                </Grid>
            </Grid>

            {/* Dialogs */}
            <ViewDialog
                fetchData={fetchData}
                txn={viewTxn}
                open={viewOpen}
                onClose={() => setViewOpen(false)}
            />

            {/* Snackbar */}
            <Snackbar open={snack.open} autoHideDuration={3000} onClose={() => setSnack((s) => ({ ...s, open: false }))} anchorOrigin={{ vertical: "bottom", horizontal: "right" }}>
                <Alert severity={snack.severity} variant="filled" onClose={() => setSnack((s) => ({ ...s, open: false }))} sx={{ borderRadius: 2 }}>
                    {snack.msg}
                </Alert>
            </Snackbar>
        </Box>
    );
}